import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";

import { cn } from "@/lib/utils";

// アプリ共通のタイポグラフィ。サイズ・字送りは variant、文字色は tone で決める。
// truncate やマージンなどのレイアウト系だけ className で足す運用にする。
const typographyVariants = cva("", {
  variants: {
    variant: {
      // 見出し
      h1: "text-3xl font-bold tracking-tight",
      h2: "text-2xl font-bold tracking-tight",
      h3: "text-xl font-semibold tracking-tight",
      h4: "text-lg font-semibold leading-snug",
      // カードタイトルなど
      title: "text-base font-semibold tracking-tight",
      // セクション見出し（小さめの全大文字ラベル）
      overline: "text-sm font-semibold uppercase tracking-wider",
      // 本文
      body: "text-sm",
      bodyLg: "text-base",
      // 補助テキスト
      label: "text-xs font-medium",
      caption: "text-xs",
    },
    tone: {
      default: "",
      muted: "text-muted-foreground",
      destructive: "text-destructive",
    },
  },
  defaultVariants: {
    variant: "body",
    tone: "default",
  },
});

// variant ごとの既定タグ。as で明示指定があればそちらを優先。
const DEFAULT_TAG: Record<string, React.ElementType> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  title: "h3",
  overline: "h2",
  body: "p",
  bodyLg: "p",
  label: "span",
  caption: "span",
};

function Typography({
  className,
  variant = "body",
  tone = "default",
  as,
  asChild = false,
  ...props
}: React.ComponentProps<"p"> &
  VariantProps<typeof typographyVariants> & {
    as?: React.ElementType;
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : as ?? DEFAULT_TAG[variant ?? "body"] ?? "p";
  return (
    <Comp
      data-slot="typography"
      className={cn(typographyVariants({ variant, tone }), className)}
      {...props}
    />
  );
}

export { Typography, typographyVariants };
